import {
  Component,
  AfterViewInit,
  ViewChild,
  ElementRef,
  OnDestroy
} from '@angular/core';
import * as maplibregl from 'maplibre-gl';
import { LocationService } from '../../../../services/location';

@Component({
  selector: 'app-herosmapview',
  templateUrl: './herosmapview.component.html',
  styleUrl: './herosmapview.component.scss'
})
export class HerosmapviewComponent implements AfterViewInit, OnDestroy {

  @ViewChild('mapEl', { static: false }) mapEl!: ElementRef;

  map!: maplibregl.Map;

  drivers: any[] = [];

  /** heroId => marker */
  markersMap = new Map<number, maplibregl.Marker>();

  refreshInterval: any;

  constructor(private _location: LocationService) { }

  // =============================
  // Lifecycle
  // =============================

  ngAfterViewInit(): void {
    this.initMap();
    this.loadDrivers();

    // 🔁 Live tracking refresh
    this.refreshInterval = setInterval(() => {
      this.loadDrivers();
    }, 5000); // كل 3 ثواني
  }

  ngOnDestroy(): void {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
    if (this.map) {
      this.map.remove();
    }
  }

  // =============================
  // Map Init
  // =============================

  initMap() {
    this.map = new maplibregl.Map({
      container: this.mapEl.nativeElement,
      style: 'https://tiles.openfreemap.org/styles/liberty',
      center: [31.159573, 29.980463],
      zoom: 13
    });

    this.map.on('load', () => {

      const layers = this.map.getStyle().layers;

      layers?.forEach(layer => {

        if (layer.type === 'symbol' && layer.layout && layer.layout['text-field']) {

          this.map.setLayoutProperty(
            layer.id,
            'text-field',
            ['get', 'name:en']   // 👈 إنجليزي بس
          );

        }

      });

    });
  }

  // =============================
  // Data
  // =============================

  loadDrivers() {
    this._location.getAllDriversLocations().subscribe({
      next: (res: any[]) => {
        this.drivers = res || [];
        this.updateLiveMarkers();
      },
      error: err => console.error(err)
    });
  }

  // =============================
  // Live Tracking Core
  // =============================

  updateLiveMarkers() {
    this.drivers.forEach(driver => {
      const lat = Number(driver.lat);
      const lng = Number(driver.lng);

      if (isNaN(lat) || isNaN(lng)) return;

      const heroId = driver.sub_delivery.id;

      // 🔹 Marker موجود → تحريك Smooth
      if (this.markersMap.has(heroId)) {
        const marker = this.markersMap.get(heroId)!;
        this.animateMarker(marker, [lng, lat]);
      }
      // 🔹 Marker جديد
      else {
        const image = driver.sub_delivery.image
          ? `https://backendproduction.blueboxpet.com/storage/app/public/${driver.sub_delivery.image}`
          : '';

        const name = driver.sub_delivery.name;

        const marker = new maplibregl.Marker({
          element: this.createHeroMarker(image, name),
          anchor: 'bottom'
        })
          .setLngLat([lng, lat])
          .addTo(this.map);

        this.markersMap.set(heroId, marker);
      }
    });
  }

  // =============================
  // Smooth Animation
  // =============================

  animateMarker(
    marker: maplibregl.Marker,
    target: [number, number]
  ) {
    const start = marker.getLngLat();
    const startTime = performance.now();
    const duration = 1000; // ms

    const animate = (time: number) => {
      const progress = Math.min((time - startTime) / duration, 1);

      const lng = start.lng + (target[0] - start.lng) * progress;
      const lat = start.lat + (target[1] - start.lat) * progress;

      marker.setLngLat([lng, lat]);

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }

  // =============================
  // Marker UI (Image + Name + Hover Zoom)
  // =============================

  createHeroMarker(imageUrl: string, name: string): HTMLElement {
    const wrapper = document.createElement('div');
    wrapper.style.display = 'flex';
    wrapper.style.flexDirection = 'column';
    wrapper.style.alignItems = 'center';
    wrapper.style.cursor = 'pointer';
    wrapper.style.transition = 'transform 0.2s ease';


    // Avatar
    const avatar = document.createElement('div');
    avatar.style.width = '46px';
    avatar.style.height = '46px';
    avatar.style.borderRadius = '50%';
    avatar.style.backgroundImage = imageUrl
      ? `url('${imageUrl}')`
      : `url('https://i.imgur.com/Qn8XHfC.png')`;
    avatar.style.backgroundSize = 'cover';
    avatar.style.backgroundPosition = 'center';
    avatar.style.border = '3px solid white';
    avatar.style.boxShadow = '0 4px 10px rgba(0,0,0,.35)';

    // Name label
    const label = document.createElement('div');
    label.innerText = name;
    label.style.marginTop = '6px';
    label.style.padding = '2px 8px';
    label.style.background = 'white';
    label.style.borderRadius = '12px';
    label.style.fontSize = '12px';
    label.style.fontWeight = '600';
    label.style.whiteSpace = 'nowrap';
    label.style.boxShadow = '0 2px 6px rgba(0,0,0,.2)';

    wrapper.appendChild(avatar);
    wrapper.appendChild(label);

    return wrapper;
  }
}
